import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Badge,
} from 'reactstrap';
import { toast } from 'react-toastify';
import {
  fetchPowerEntry,
  submitPowerEntry,
  markFramePlano,
} from '../../services/api/cashless-vision';

// Accepted ranges — mirror the backend rule engine's POWER_LIMITS
const LIMITS = {
  spherical: { min: -30, max: 30, label: 'SPH' },
  cylindrical: { min: -10, max: 10, label: 'CYL' },
  addPower: { min: 0, max: 4, label: 'Add' },
  axis: { min: 0, max: 180, label: 'Axis' },
};
const STEP = 0.25;

const EMPTY_EYE = { spherical: '', cylindrical: '', addPower: '', axis: '' };

const isQuarterStep = (value) => Math.abs((parseFloat(value) / STEP) % 1) < 1e-9;

const eyeHasData = (eye) =>
  Object.values(eye).some((value) => value !== '' && value !== null && value !== undefined);

// Per-field inline validation; returns an error message or null
const fieldError = (field, value) => {
  if (value === '' || value === null) return null;
  const num = parseFloat(value);
  if (isNaN(num)) return 'Enter a number';
  const { min, max } = LIMITS[field];
  if (num < min || num > max) return `Must be between ${min} and ${max}`;
  if (field === 'axis') {
    return Number.isInteger(num) ? null : 'Whole number only';
  }
  return isQuarterStep(value) ? null : `Use steps of ${STEP} (e.g. -1.25)`;
};

const eyeErrors = (eye) => {
  const errors = {};
  ['spherical', 'cylindrical', 'addPower', 'axis'].forEach((field) => {
    const error = fieldError(field, eye[field]);
    if (error) errors[field] = error;
  });
  if (!errors.spherical && eyeHasData(eye) && eye.spherical === '') {
    errors.spherical = 'SPH is required';
  }
  return errors;
};

const formatIcdCodes = (codes) =>
  (codes || [])
    .map((code) => `${code.icdCode}${code.diagnosis ? ` (${code.diagnosis})` : ''}`)
    .join(', ');

const EyeFields = ({ eyeLabel, eye, errors, onChange, disabled }) => (
  <Col md={6}>
    <div style={{ fontWeight: 600, marginBottom: '8px' }}>{eyeLabel} Eye</div>
    {['spherical', 'cylindrical', 'addPower', 'axis'].map((field) => (
      <FormGroup key={field}>
        <Label style={{ fontSize: '12px', marginBottom: '2px' }}>
          {LIMITS[field].label}
          {field === 'spherical' && eyeHasData(eye) ? ' *' : ''}
        </Label>
        <Input
          type="number"
          step={field === 'axis' ? 1 : STEP}
          min={LIMITS[field].min}
          max={LIMITS[field].max}
          value={eye[field]}
          disabled={disabled}
          invalid={Boolean(errors[field])}
          placeholder={LIMITS[field].label}
          onChange={(event) => onChange(field, event.target.value)}
        />
        {errors[field] && (
          <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>
            {errors[field]}
          </div>
        )}
      </FormGroup>
    ))}
  </Col>
);

/**
 * Power Entry form (SPH / CYL / Add per eye). The backend rule engine derives
 * and tags the ICD-10 code(s) from the submitted power.
 */
const PowerEntryModal = ({ isOpen, toggle, requestId, onSuccess }) => {
  const [rightEye, setRightEye] = useState(EMPTY_EYE);
  const [leftEye, setLeftEye] = useState(EMPTY_EYE);
  const [icdStatus, setIcdStatus] = useState(null);
  const [icdCodes, setIcdCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadPowerEntry = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchPowerEntry(requestId);
      // empty eye data with icdStatus 'pending' is the normal state for a fresh
      // order — only a non-success message is an actual load failure
      if (!response || response.message !== 'success') {
        console.error('power-entry load returned non-success', response);
        toast.error('Failed to load power details');
        return;
      }
      const data = response.data || {};
      const toForm = (eye) => ({
        spherical: eye && eye.spherical !== null && eye.spherical !== undefined ? String(eye.spherical) : '',
        cylindrical: eye && eye.cylindrical !== null && eye.cylindrical !== undefined ? String(eye.cylindrical) : '',
        addPower: eye && eye.addPower !== null && eye.addPower !== undefined ? String(eye.addPower) : '',
        axis: eye && eye.axis !== null && eye.axis !== undefined ? String(eye.axis) : '',
      });
      setRightEye(toForm(data.eye && data.eye.right));
      setLeftEye(toForm(data.eye && data.eye.left));
      setIcdStatus(data.icdStatus || null);
      setIcdCodes(data.icdCodes || []);
    } catch (error) {
      // leave the empty form usable — the modal's job is entering power, not reading it
      console.error('power-entry load failed', error);
      toast.error(`Failed to load saved power details${error && error.message ? ` (${error.message})` : ''}. You can still enter power and save.`);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    if (isOpen && requestId) {
      loadPowerEntry();
    }
  }, [isOpen, requestId, loadPowerEntry]);

  const rightErrors = eyeErrors(rightEye);
  const leftErrors = eyeErrors(leftEye);
  const hasErrors = Object.keys(rightErrors).length > 0 || Object.keys(leftErrors).length > 0;
  const hasAnyData = eyeHasData(rightEye) || eyeHasData(leftEye);

  const handleSubmit = async () => {
    if (!hasAnyData) {
      toast.error('Enter power details for at least one eye');
      return;
    }
    if (hasErrors) {
      toast.error('Fix the highlighted power values first');
      return;
    }

    const toPayload = (eye) => ({
      spherical: parseFloat(eye.spherical),
      cylindrical: eye.cylindrical === '' ? null : parseFloat(eye.cylindrical),
      addPower: eye.addPower === '' ? null : parseFloat(eye.addPower),
      axis: eye.axis === '' ? null : parseInt(eye.axis, 10),
    });
    const eye = {};
    if (eyeHasData(rightEye)) eye.right = toPayload(rightEye);
    if (eyeHasData(leftEye)) eye.left = toPayload(leftEye);

    setSubmitting(true);
    try {
      const response = await submitPowerEntry(requestId, eye);
      const data = (response && response.data) || {};
      const codesLabel = formatIcdCodes(data.icdCodes);
      if (data.icdStatus === 'not-applicable-frame-plano') {
        toast.success('Power saved — 0-power order tagged as Not Applicable (Frame/Plano)');
      } else {
        toast.success(`Power saved — ICD tagged: ${codesLabel || data.icdStatus}`);
      }
      toggle();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(
        (error.response && error.response.data && (error.response.data.errorMessage || error.response.data.message)) ||
          'Failed to save power details'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkFramePlano = async () => {
    setSubmitting(true);
    try {
      await markFramePlano(requestId);
      toast.success('Order marked as Not Applicable – Frame/Plano');
      toggle();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(
        (error.response && error.response.data && (error.response.data.errorMessage || error.response.data.message)) ||
          'Failed to mark order as Frame/Plano'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = () => {
    if (icdStatus === 'tagged') {
      return <Badge color="success">Tagged: {formatIcdCodes(icdCodes)}</Badge>;
    }
    if (icdStatus === 'not-applicable-frame-plano') {
      return <Badge color="secondary">Not Applicable – Frame/Plano</Badge>;
    }
    return <Badge color="warning">ICD Pending</Badge>;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>Power Entry — RID {requestId}</ModalHeader>
      <ModalBody>
        <div style={{ marginBottom: '12px' }}>
          ICD-10 Status: {statusBadge()}
        </div>
        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '12px' }}>
          Enter the power values from the vision card. The ICD-10 diagnosis code(s) are derived
          automatically — SPH − / + sets Myopia / Hypermetropia, CYL adds Astigmatism, Add adds
          Presbyopia.
        </div>
        <Row>
          <EyeFields eyeLabel="Right" eye={rightEye} errors={rightErrors} onChange={(field, value) => setRightEye((prev) => ({ ...prev, [field]: value }))} disabled={loading || submitting} />
          <EyeFields eyeLabel="Left" eye={leftEye} errors={leftErrors} onChange={(field, value) => setLeftEye((prev) => ({ ...prev, [field]: value }))} disabled={loading || submitting} />
        </Row>
      </ModalBody>
      <ModalFooter style={{ justifyContent: 'space-between' }}>
        <Button color="secondary" outline onClick={handleMarkFramePlano} disabled={submitting || loading}>
          Not Applicable – Frame/Plano
        </Button>
        <div>
          <Button color="light" onClick={toggle} style={{ marginRight: '8px' }} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={submitting || loading || hasErrors || !hasAnyData}>
            {submitting ? 'Saving...' : 'Save & Tag ICD'}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default PowerEntryModal;
